import { logError } from "@/lib/error-handling";
import { apiClient } from "@/services/api";
import {
  FeedbackWithAuditDto,
  PublicOrganizationFeedbackThemeSummary,
} from "@/types";
import { CreateFeedbackInput, UpdateFeedbackInput } from "@/schemas";
import { RelatedObjectType } from "@/enums";

export type CreateFeedbackRequest = CreateFeedbackInput;
export type UpdateFeedbackRequest = UpdateFeedbackInput;

/**
 * Creates a new feedback by sending the provided data to the backend.
 *
 * @param data - The data required to create a new feedback.
 * @returns A promise that resolves with the response data from the backend.
 * @throws Will throw an error if the request fails or if validation fails.
 */
export async function createFeedback(
  data: CreateFeedbackRequest,
  accountId: string
): Promise<FeedbackWithAuditDto> {
  try {
    return await apiClient<FeedbackWithAuditDto>(
      `/api/accounts/${accountId}/feedbacks`,
      {
        method: "POST",
        body: JSON.stringify(data),
      }
    );
  } catch (error) {
    logError(error, "createFeedback");
    throw error;
  }
}

/**
 * Fetches all feedbacks for a specific account.
 *
 * @param accountId - The ID of the account to fetch feedbacks for
 * @returns A list of feedbacks.
 * @throws An error if the request fails.
 */
export async function fetchFeedbacksByAccountId(
  accountId: string
): Promise<FeedbackWithAuditDto[]> {
  try {
    return await apiClient<FeedbackWithAuditDto[]>(
      `/api/accounts/${accountId}/feedbacks`,
      {
        method: "GET",
        cache: "no-store",
      }
    );
  } catch (error) {
    logError(error, "fetchFeedbacksByAccountId");
    throw error;
  }
}

/**
 * Fetches a single feedback by ID.
 *
 * @param feedbackId - The ID of the feedback to fetch
 * @returns The feedback data with audit information.
 * @throws An error if the request fails.
 */
export async function fetchFeedbackById(
  feedbackId: string
): Promise<FeedbackWithAuditDto> {
  try {
    return await apiClient<FeedbackWithAuditDto>(
      `/api/feedbacks/${feedbackId}`,
      {
        method: "GET",
        cache: "no-store",
      }
    );
  } catch (error) {
    logError(error, "fetchFeedbackById");
    throw error;
  }
}

/**
 * Updates an existing feedback by sending the provided data to the backend.
 *
 * @param feedbackId - The ID of the feedback to update
 * @param data - The data to update the feedback with
 * @returns A promise that resolves with the updated feedback data from the backend.
 * @throws Will throw an error if the request fails or if validation fails.
 */
export async function updateFeedback(
  feedbackId: string,
  data: UpdateFeedbackRequest
): Promise<FeedbackWithAuditDto> {
  try {
    return await apiClient<FeedbackWithAuditDto>(
      `/api/feedbacks/${feedbackId}`,
      {
        method: "PATCH",
        body: JSON.stringify(data),
      }
    );
  } catch (error) {
    logError(error, "updateFeedback");
    throw error;
  }
}

/**
 * Deletes a feedback by its ID.
 * @param feedbackId - The ID of the feedback to delete
 * @returns A promise that resolves when the feedback is deleted.
 * @throws An error if the request fails or if the feedback is not found.
 */
export async function deleteFeedback(feedbackId: string): Promise<void> {
  try {
    await apiClient<void>(`/api/feedbacks/${feedbackId}`, {
      method: "DELETE",
    });
  } catch (error) {
    logError(error, "deleteFeedback");
    throw error;
  }
}

/**
 * Fetches all feedbacks across all accounts for global dashboard statistics.
 * This aggregates feedbacks from all accounts the user has access to.
 *
 * @returns A list of all feedbacks across all accounts.
 * @throws An error if the request fails.
 */
export async function fetchAllFeedbacks(): Promise<FeedbackWithAuditDto[]> {
  try {
    return await apiClient<FeedbackWithAuditDto[]>("/api/feedbacks/all", {
      method: "GET",
      cache: "no-store",
    });
  } catch (error) {
    logError(error, "fetchAllFeedbacks");
    throw error;
  }
}

/**
 * Fetches the organization-wide AI theme summary for the feedback page.
 *
 * @returns The organization feedback theme summary, or null when no recent themes are available.
 * @throws An error if the request fails.
 */
export async function fetchOrganizationFeedbackThemes(): Promise<PublicOrganizationFeedbackThemeSummary | null> {
  try {
    return await apiClient<PublicOrganizationFeedbackThemeSummary | null>(
      "/api/feedbacks/themes",
      {
        method: "GET",
        cache: "no-store",
      }
    );
  } catch (error) {
    logError(error, "fetchOrganizationFeedbackThemes");
    throw error;
  }
}

/**
 * Fetches related objects for a feedback by type(s).
 *
 * @param feedbackId - The ID of the feedback
 * @param types - Array of related object types (e.g., ["ACTIVITY", "GOAL"])
 * @returns An object with arrays of related objects grouped by type.
 * @throws An error if the request fails.
 */
export async function fetchFeedbackRelatedObjects(
  feedbackId: string,
  types: RelatedObjectType[]
): Promise<Record<string, unknown[]>> {
  try {
    if (!feedbackId || !types.length) return {};
    const params = new URLSearchParams();
    types.forEach((t) => params.append("type", t));
    return await apiClient<Record<string, unknown[]>>(
      `/api/feedbacks/${feedbackId}/related-objects?${params.toString()}`,
      {
        method: "GET",
      }
    );
  } catch (error) {
    logError(error, "fetchFeedbackRelatedObjects");
    throw error;
  }
}
